// @ts-nocheck

import React, { useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import styled from 'styled-components';

import { Target } from '../../../client';
import Modal from '../../../components/Modal';
import WinaLoader from '../../../components/WinaLoader';
import { ModalContentSpacing } from '../../../constants/ui/modal';
import useRouter from '../../../hooks/useRouter';
import useShallowEqualSelector from '../../../hooks/useShallowEqualSelector';
import { RoutePatterns } from '../../../types/routes';
import { showWebsiteLoginForm } from '../../../utils';
import RoutesRedirectionFactory from '../../../utils/routes/RoutesRedirectionFactory';
import KLBadgeDetailsContainer from '../containers/KLBadgeDetailsContainer';
import KLEnrollmentContainer from '../containers/KLEnrollmentContainer';
import KLHomeContainer from '../containers/KLHomeContainer';
import KLNotificationsViewerContainer from '../containers/KLNotificationsViewerContainer';
import KLOrientationStepDetailsContainer from '../containers/KLOrientationStepDetailsContainer';
import KLRoundRulesContainer from '../containers/KLRoundRulesContainer';
import { selectIsKLEnabled } from '../selectors/runtime';
import { KLUserStatuses } from '../types/user';

const StyledWinaLoader = styled(WinaLoader)`
  margin: 0;
  margin-top: 44.1vh;
  transform: translateY(-50%);
`;

const ValidatedKLRouterSwitch = ({ showNotificationModal }) => {
  return (
    <>
      <Switch>
        <Route path={RoutePatterns.KL} exact>
          <KLHomeContainer />
        </Route>
        <Route path={RoutePatterns.KL_ENROLLMENT} exact>
          <KLEnrollmentContainer />
        </Route>
        <Route path={RoutePatterns.KL_ROUND_RULES} exact>
          <KLRoundRulesContainer />
        </Route>
        <Route path={RoutePatterns.KL_BADGE_DETAILS} exact>
          <KLBadgeDetailsContainer />
        </Route>
        <Route path={RoutePatterns.KL_ORIENTATION_STEP_DETAIL} exact>
          <KLOrientationStepDetailsContainer />
        </Route>
      </Switch>
      {showNotificationModal ? <KLNotificationsViewerContainer /> : null}
    </>
  );
};

const ValidatedKLRouterSwitchContainer = ({ showNotificationModal }) => {
  const { history } = useRouter();
  const { isConnected, KLUserStatus, didReceivedUserData, isKLEnabled } = useShallowEqualSelector((state) => ({
    isConnected: state.user?.connected ?? false,
    didReceivedUserData: state.user?.didReceivedUserData,
    KLUserStatus: state.kl.user.status,
    isKLEnabled: selectIsKLEnabled(state),
  }));
  const isConnectedRef = useRef(isConnected);
  const KLUserStatusRef = useRef(KLUserStatus);

  useEffect(() => {
    isConnectedRef.current = isConnected;
  }, [isConnected]);
  useEffect(() => {
    KLUserStatusRef.current = KLUserStatus;
  }, [KLUserStatus]);

  useLayoutEffect(() => {
    if (!isKLEnabled) {
      history.push(RoutesRedirectionFactory.getHomeRouteConfig());
    }

    if (!isConnectedRef.current) {
      if (Target.__MOBILE__) {
        history.push({
          pathname: RoutesRedirectionFactory.getHomeRoutePathname(),
          ...RoutesRedirectionFactory.getLoginRouteConfig({
            state: {
              modal: true,
            },
          }),
        });
      } else {
        showWebsiteLoginForm();

        history.push(RoutesRedirectionFactory.getHomeRouteConfig());
      }
    }

    if (didReceivedUserData) {
      if (!KLUserStatusRef.current || KLUserStatusRef.current === KLUserStatuses.OFF) {
        history.push(RoutesRedirectionFactory.getKLEnrollementRouteConfig());
      }
    }
  }, [didReceivedUserData, history, isKLEnabled]);

  return didReceivedUserData ? (
    <ValidatedKLRouterSwitch showNotificationModal={showNotificationModal} />
  ) : (
    <StyledWinaLoader />
  );
};

const Wrapper = styled.div`
  font-family: 'BettingKalam';
  color: white;
`;

type KLRouterSwitchProps = {
  className?: string;
};

const KLRouterSwitch = ({ className = '' }: KLRouterSwitchProps) => {
  const { history } = useRouter();
  const showNotificationModal = useSelector((state) => !!state.kl.misc?.tmpNotifications?.length);

  const onCloseModal = useCallback(() => {
    history.replace(RoutesRedirectionFactory.getHomeRouteConfig());
  }, [history]);

  return (
    <Wrapper className={className}>
      <Switch>
        <Route path={RoutePatterns.KL}>
          {!Target.__MOBILE__ ? (
            <Modal
              backgroundColor={'#000000'}
              contentSpacing={ModalContentSpacing.NONE}
              hasOverlay
              closeOnOutsideClick
              closeOnEsc
              onClose={onCloseModal}
              withBorder
              hasCloseButton={!showNotificationModal}
              disableScrollView
            >
              <ValidatedKLRouterSwitchContainer showNotificationModal={showNotificationModal} />
            </Modal>
          ) : (
            <ValidatedKLRouterSwitchContainer showNotificationModal={showNotificationModal} />
          )}
        </Route>
      </Switch>
    </Wrapper>
  );
};

export default KLRouterSwitch;
